import type { FC, PropsWithChildren } from 'react';

import { UserGroupIcon } from '@heroicons/react/24/outline';
import { useRouteLoaderData } from '@remix-run/react';

import type { loader as communityLoader } from '~/routes/community';

import { Avatar } from './ui/Avatar';

import { useCommunityLogoUrl } from '../_hooks/useCommunityLogoUrl';
import { hasLightTextOnBackground } from '../_utils/communityTheme';

export const GateBannerCard: FC<PropsWithChildren> = ({ children }) => {
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const textOnBackground = layoutData?.community?.text_on_background_color;
  // Dark themes let more of the grain through, or the texture reads as flat noise on a dark surface.
  const backgroundOverlayOpacity = hasLightTextOnBackground(textOnBackground) ? 0.8 : 0.9;

  return (
    <section className="relative overflow-hidden rounded-3xl border border-community-text-on-bg/10 p-6 text-community-text-on-bg shadow-[0px_4px_8px_0px_rgba(0,0,0,0.1),0px_2px_6px_0px_rgba(0,0,0,0.04)]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[url('/images/community-join-texture.webp')] bg-cover bg-center"
      >
        {/* Ignores any alpha the background carries, or the texture shows through raw. */}
        <div
          className="absolute inset-0 bg-[rgb(var(--community-bg-rgb))]"
          style={{ opacity: backgroundOverlayOpacity }}
        />
      </div>
      <div className="relative mx-auto flex max-w-[460px] flex-col items-center text-center">{children}</div>
    </section>
  );
};

export const GateBannerLogo: FC<{ alt: string }> = ({ alt }) => {
  const logoUrl = useCommunityLogoUrl();

  return logoUrl ? (
    <Avatar src={logoUrl} alt={alt} className="size-12" />
  ) : (
    <UserGroupIcon className="size-8 text-community-text-on-bg/90" aria-hidden="true" role="presentation" />
  );
};

export const GateBannerTitle: FC<PropsWithChildren> = ({ children }) => (
  <h2 className="mt-3 text-[32px] font-semibold leading-[36px] tracking-[-0.64px] md:text-[40px] md:leading-[44px] md:tracking-[-0.8px]">
    {children}
  </h2>
);

export const GateBannerNote: FC<PropsWithChildren> = ({ children }) => (
  <p className="mt-5 max-w-[380px] rounded-xl bg-community-text-on-bg/[0.06] px-4 py-3 text-sm leading-5 text-community-text-on-bg/75">
    {children}
  </p>
);
